/**
 * GIWAHS Adaptive Journey Core Engine — TransitionEngine (AJIP-16.02.003 · WS5)
 * ---------------------------------------------------------------------
 * Deterministic transition evaluator.
 *
 * Properties (per directive)
 *   Predictable  — same inputs always produce the same output.
 *   Auditable    — returns a full decision trace.
 *   Recoverable  — rejection returns a typed error, never throws.
 *   Repeatable   — pure function, no side effects, no clock reads.
 *
 * A transition is matched when there exists a rule in the journey
 * definition whose (from, event) tuple equals the requested pair.
 * Multiple rules matching the same tuple is a configuration error and
 * rejected upstream in JourneyConfig.
 *
 * Note on future AI influence (per Founder Ruling 02)
 *   The deterministic backbone shipped in this package is complete on
 *   its own. Formal extension interfaces (see interfaces/) will allow
 *   future packages to supply an AIRecommendationProvider. This engine
 *   does not import, instantiate, or infer an AI provider. A future
 *   package integrating an AI provider MUST NOT weaken determinism.
 */
import { isValidState, isTerminalState } from './JourneyState';
import { InvalidTransitionError, ConfigError } from './JourneyErrors';

const evaluateTransition = ({ from, event, journey }) => {
  const trace = [];
  const decide = (ok, to, reason) => Object.freeze({
    ok,
    from,
    event,
    to: ok ? to : null,
    reason,
    trace: Object.freeze(trace.slice()),
    journeyIdentifier: journey ? journey.identifier : null,
  });

  if (!journey || typeof journey !== 'object') {
    return decide(false, null, 'JOURNEY_MISSING');
  }
  trace.push({ step: 'journey_present', ok: true });

  if (!isValidState(from)) {
    return decide(false, null, 'FROM_INVALID');
  }
  trace.push({ step: 'from_valid', ok: true });

  if (isTerminalState(from)) {
    return decide(false, null, 'FROM_TERMINAL');
  }
  trace.push({ step: 'from_not_terminal', ok: true });

  if (typeof event !== 'string' || event.length === 0) {
    return decide(false, null, 'EVENT_INVALID');
  }
  trace.push({ step: 'event_valid', ok: true });

  const matches = journey.transitions.filter((t) => t.from === from && t.event === event);
  if (matches.length === 0) {
    return decide(false, null, 'NO_MATCHING_RULE');
  }
  if (matches.length > 1) {
    // Guardrail. Should never happen because config validation forbids
    // duplicate transitions upstream, but check defensively.
    return decide(false, null, 'AMBIGUOUS_RULE');
  }
  trace.push({ step: 'rule_matched', ok: true, target: matches[0].to });
  return decide(true, matches[0].to, 'OK');
};

const assertNoDuplicateTransitions = (transitions, identifier) => {
  const seen = new Set();
  for (const t of transitions) {
    const key = `${t.from}::${t.event}`;
    if (seen.has(key)) {
      throw new ConfigError(
        `Journey '${identifier}': duplicate transition for (${t.from}, ${t.event})`,
        { transition: t },
      );
    }
    seen.add(key);
  }
};

const raiseInvalidTransition = (result) => {
  throw new InvalidTransitionError(
    `Invalid transition from ${result.from} on event '${result.event}': ${result.reason}`,
    { result },
  );
};

export { evaluateTransition, assertNoDuplicateTransitions, raiseInvalidTransition };
